import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import { normalizeOptionsPnlAnalysisData } from "../../../api/services/optionsPnlQuery";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import WalletTransferModals from "../WalletPage/WalletTransferModals";
import { useWalletTransfer } from "../WalletPage/useWalletTransfer";
import useOptionsWebSocket from "../options/useOptionsWebSocket";
import { resolveOptionsAccountUnrealizedPnl } from "../options/optionsDataHelpers";
import "../FundingAccountPage/fundingAccount.css";
import "../AssetOverview/assetOverview.css";

function parseBal(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function fmt(val, decimals = 4) {
  const n = parseBal(val);
  if (n === 0) return decimals >= 8 ? "0.00000000" : "0.0000";
  return parseFloat(n.toFixed(decimals)).toString();
}

function fmtUsd(val) {
  const n = parseBal(val);
  return `$${n.toFixed(2)}`;
}

function pnlClass(val) {
  const n = parseFloat(val);
  if (!Number.isFinite(n) || n === 0) return "text-success";
  return n > 0 ? "text-success" : "text-danger";
}

function OptionsTableEmptyBody({ colSpan }) {
  return (
    <tr className="no-data-row no-data-row2">
      <td colSpan={colSpan}>
        <div className="no-data-wrapper">
          <div className="no_data_vector">
            <img src="/images/no_data_vector.svg" className="dark_img" alt="" />
            <img src="/images/no_data_vector_light.png" className="light_img" alt="" />
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function OptionsAccountPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("assets");
  const [showBalance, setShowBalance] = useState(true);
  const [positions, setPositions] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [dailyPnlCard, setDailyPnlCard] = useState(null);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingDailyPnl, setLoadingDailyPnl] = useState(true);

  const assetIcon = useMemo(() => {
    const path = walletData?.icon_path;
    return path ? resolvePublicUploadUrl(path) : "";
  }, [walletData?.icon_path]);

  const token = useMemo(() => {
    try {
      return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    } catch {
      return "";
    }
  }, []);

  const {
    accountUpdate,
    userPositions,
    isUserPositionsLoading,
  } = useOptionsWebSocket({
    token,
    enabled: Boolean(token),
  });

  const fetchWallet = useCallback(async () => {
    setLoadingWallet(true);
    try {
      const walletRes = await AuthService.optionsWallet();
      if (walletRes?.success) setWalletData(walletRes.data ?? null);
      else setWalletData(null);
    } catch {
      setWalletData(null);
    } finally {
      setLoadingWallet(false);
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    setLoadingPositions(true);
    try {
      const posRes = await AuthService.optionsOpenPositions();
      if (posRes?.success) {
        setPositions(Array.isArray(posRes.data) ? posRes.data : posRes.data?.positions ?? []);
      } else {
        setPositions([]);
      }
    } catch {
      setPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  }, []);

  const fetchDailyPnl = useCallback(async () => {
    setLoadingDailyPnl(true);
    try {
      const res = await AuthService.optionsPnlAnalysis({ period: "7d" });
      if (res?.success) {
        const normalized = normalizeOptionsPnlAnalysisData(res.data);
        setDailyPnlCard(normalized?.summary_cards?.daily ?? null);
      } else {
        setDailyPnlCard(null);
      }
    } catch {
      setDailyPnlCard(null);
    } finally {
      setLoadingDailyPnl(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchWallet(), fetchPositions(), fetchDailyPnl()]);
  }, [fetchWallet, fetchPositions, fetchDailyPnl]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const closeTransferModal = useCallback(() => {
    const el = document.getElementById("walletTransferModal");
    if (el && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el).hide();
    } else if (el) {
      el.classList.remove("show");
      el.style.display = "none";
      document.body.classList.remove("modal-open");
    }
  }, []);

  const walletTransfer = useWalletTransfer({
    initialFrom: "spot",
    initialTo: "options",
    onTransferSuccess: async () => {
      await refreshAll();
      closeTransferModal();
    },
  });

  const restAvailable = parseBal(walletData?.available_balance);
  const restInOrder = parseBal(walletData?.locked_balance);
  const restTotal = parseBal(walletData?.total_balance) || restAvailable + restInOrder;

  /** Live balances from options:account WebSocket (same as options trade page). */
  const hasLiveAccount = Boolean(accountUpdate);
  const available = parseBal(accountUpdate?.available_balance ?? restAvailable);
  const inOrder = parseBal(accountUpdate?.in_order ?? restInOrder);
  const totalEquity = parseBal(accountUpdate?.total_equity ?? restTotal);
  const marginBalance = parseBal(accountUpdate?.margin_balance ?? totalEquity);
  const walletTotal = available + inOrder;
  const wsPositionsReady = !isUserPositionsLoading;
  const restPositionsReady = !loadingPositions;
  const unrealizedPnl = wsPositionsReady
    ? resolveOptionsAccountUnrealizedPnl(accountUpdate, userPositions, true)
    : restPositionsReady
      ? resolveOptionsAccountUnrealizedPnl(accountUpdate, positions, true)
      : parseBal(accountUpdate?.unrealized_pnl ?? (totalEquity - walletTotal));
  const loading = !hasLiveAccount && loadingWallet && !walletData;
  const dailyPnl = parseBal(dailyPnlCard?.pnl_usdt);
  const dailyPnlPct = parseBal(dailyPnlCard?.pnl_pct);
  const dailyPnlLoading = loadingDailyPnl && dailyPnlCard == null;

  const mask = useCallback(
    (value) => (showBalance ? value : "****"),
    [showBalance]
  );

  const openTransfer = () => {
    walletTransfer.openTransferModal({ from: "spot", to: "options" });
    const el = document.getElementById("walletTransferModal");
    if (el && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el).show();
    } else if (el) {
      el.classList.add("show");
      el.style.display = "block";
      document.body.classList.add("modal-open");
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="dashboard_right funding-account margin-pro-page options-account-page">
      <div className="funding-account__summarycard">
        <div className="d-flex marginwallet_card flex-wrap">
          <div className="funding-account__summary-main">
            <div className="funding-account__summary-top">
              <div className="funding-account__totals-label h4">
                Total Equity
                <button
                  type="button"
                  onClick={() => setShowBalance((prev) => !prev)}
                  aria-label={showBalance ? "Hide balances" : "Show balances"}
                  style={{ marginLeft: 8, border: "none", background: "transparent", padding: 2, cursor: "pointer", verticalAlign: "middle" }}
                >
                  <i className={showBalance ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="funding-account__summary-amount">
              <div className="funding-account__summary-num">
                {loading ? "—" : mask(fmt(totalEquity, 4))}
              </div>
            </div>
            <div className="funding-account__summary-sub">
              ≈ {loading ? "—" : mask(fmtUsd(totalEquity))}
            </div>
            <div className="funding-account__summary-pnl">
              <span>Daily PNL</span>
              <strong className={pnlClass(dailyPnl)}>
                {loading || dailyPnlLoading
                  ? "—"
                  : mask(`${dailyPnl >= 0 ? "+" : "-"}${fmtUsd(Math.abs(dailyPnl))} (${dailyPnlPct >= 0 ? "+" : ""}${dailyPnlPct.toFixed(2)}%)`)}
              </strong>
            </div>
          </div>

          <div className="funding-account__summary-grid">
            <div className="funding-account__summary-stat">
              <div
                className="funding-account__summary-stat-k"
                style={{ borderBottom: "1px dashed #b7bdc6", display: "inline-block" }}
              >
                Margin Balance (USDT)
              </div>
              <div className="funding-account__summary-stat-v">
                {loading ? "—" : mask(fmt(marginBalance, 4))}
              </div>
              <div className="funding-account__summary-sub">
                ≈ {loading ? "—" : mask(fmtUsd(marginBalance))}
              </div>
            </div>
            <div className="funding-account__summary-stat funding-account__summary-stat--right">
              <div
                className="funding-account__summary-stat-k"
                style={{ borderBottom: "1px dashed #b7bdc6", display: "inline-block" }}
              >
                Unrealized PNL (USDT)
              </div>
              <div className={`funding-account__summary-stat-v ${pnlClass(unrealizedPnl)}`}>
                {loading ? "—" : mask(fmt(unrealizedPnl, 4))}
              </div>
              <div className="funding-account__summary-sub">
                ≈ {loading ? "—" : mask(fmtUsd(unrealizedPnl))}
              </div>
            </div>
          </div>

          <div className="funding-account__header-actions options-account__summary-actions">
            <button
              type="button"
              className="funding-account__pill options-account__btn-transfer"
              onClick={openTransfer}
            >
              Transfer
            </button>
            <button
              type="button"
              className="funding-account__pill options-account__btn-history"
              onClick={() => navigate("/user_profile/wallet/options/pnl-analysis")}
            >
              PNL Analysis
            </button>
            <button
              type="button"
              className="funding-account__pill options-account__btn-history"
              onClick={() => navigate("/options/BTC_USDT")}
            >
              Transaction History
            </button>
          </div>
        </div>
      </div>

      <div className="funding-account__tabs">
        <button
          type="button"
          className={`funding-account__tab ${tab === "assets" ? "is-active" : ""}`}
          onClick={() => setTab("assets")}
        >
          Assets
        </button>
        <button
          type="button"
          className={`funding-account__tab ${tab === "positions" ? "is-active" : ""}`}
          onClick={() => setTab("positions")}
        >
          Positions
        </button>
      </div>

      {tab === "assets" && (
        <div className="funding-account__tablewrap">
          <table className="funding-account__table funding-account__table--futures-assets">
            <thead>
              <tr>
                <th>Assets</th>
                <th className="ta-r">
                  <span style={{ borderBottom: "1px dashed #b7bdc6" }}>Margin Balance</span>
                </th>
                <th className="ta-r">
                  <span style={{ borderBottom: "1px dashed #b7bdc6" }}>Available (USDT)</span>
                </th>
                <th className="ta-r">
                  <span style={{ borderBottom: "1px dashed #b7bdc6" }}>In Order (USDT)</span>
                </th>
                <th className="ta-r">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-3 text-muted">Loading…</td></tr>
              ) : (
                <tr>
                  <td data-label="Assets">
                    <div className="funding-account__coin flex-grow-1">
                      {assetIcon ? (
                        <img src={assetIcon} alt="" width={24} height={24} style={{ borderRadius: "50%" }} />
                      ) : (
                        <span className="funding-account__coin-dot" aria-hidden="true" />
                      )}
                      <div>
                        <div className="funding-account__coin-sym">{walletData?.asset || "USDT"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="ta-r" data-label="Margin Balance">{mask(fmt(walletTotal, 8))}</td>
                  <td className="ta-r" data-label="Available (USDT)">{mask(fmt(available, 8))}</td>
                  <td className="ta-r" data-label="In Order (USDT)">{mask(fmt(inOrder, 8))}</td>
                  <td className="ta-r" data-label="Action">
                    <button
                      type="button"
                      className="funding-account__action funding-account__action--btn"
                      onClick={openTransfer}
                    >
                      Transfer
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "positions" && (
        <div className="funding-account__tablewrap">
          <table className="funding-account__table funding-account__table--futures">
            <thead>
              <tr>
                <th>Contract</th>
                <th className="ta-r">Side</th>
                <th className="ta-r">Size</th>
                <th className="ta-r">Avg Price</th>
                <th className="ta-r">Mark Price</th>
                <th className="ta-r">Unrealized PNL</th>
                <th className="ta-r">Action</th>
              </tr>
            </thead>
            <tbody>
              {loadingPositions ? (
                <tr><td colSpan={7} className="text-center py-3 text-muted">Loading…</td></tr>
              ) : positions.length === 0 ? (
                <OptionsTableEmptyBody colSpan={7} />
              ) : positions.map((pos) => {
                const upnl = parseBal(pos.unrealized_pnl);
                return (
                  <tr key={pos.symbol}>
                    <td className="funding-account__pair" data-label="Contract">
                      <div className="funding-account__coin flex-grow-1">
                        <span className="funding-account__coin-dot" aria-hidden="true" />
                        <div>
                          <div className="funding-account__coin-sym">{pos.symbol}</div>
                          <div className="funding-account__coin-name">{pos.option_type || pos.underlying || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="ta-r" data-label="Side">{pos.side || "—"}</td>
                    <td className="ta-r" data-label="Size">{fmt(pos.quantity, 4)}</td>
                    <td className="ta-r" data-label="Avg Price">{fmt(pos.avg_price, 2)}</td>
                    <td className="ta-r" data-label="Mark Price">{fmt(pos.mark_price, 2)}</td>
                    <td className={`ta-r ${pnlClass(upnl)}`} data-label="Unrealized PNL">
                      {upnl >= 0 ? "+" : ""}{upnl.toFixed(2)} USDT
                    </td>
                    <td className="ta-r" data-label="Action">
                      <Link to="/options/BTC_USDT" className="funding-account__action">
                        Trade
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <WalletTransferModals
        fromWalletType={walletTransfer.fromWalletType}
        setFromWalletType={walletTransfer.setFromWalletType}
        toWalletType={walletTransfer.toWalletType}
        setToWalletType={walletTransfer.setToWalletType}
        availableWalletTypes={walletTransfer.availableWalletTypes}
        walletLabelsMap={walletTransfer.walletLabelsMap}
        selectedCurrency={walletTransfer.selectedCurrency}
        setSelectedCurrency={walletTransfer.setSelectedCurrency}
        transferAmount={walletTransfer.transferAmount}
        setTransferAmount={walletTransfer.setTransferAmount}
        transferable={walletTransfer.transferable}
        balanceLoading={walletTransfer.balanceLoading}
        sourceHint={walletTransfer.sourceHint}
        handleInterchangeWallet={walletTransfer.handleInterchangeWallet}
        handleTransfer={walletTransfer.handleTransfer}
        currencyData={walletTransfer.currencyData}
        fiveDecimal={walletTransfer.fiveDecimal}
        marginInvolved={walletTransfer.marginInvolved}
        marginPairs={walletTransfer.marginPairs}
        selectedMarginPair={walletTransfer.selectedMarginPair}
        setSelectedMarginPair={walletTransfer.setSelectedMarginPair}
        marginAssetType={walletTransfer.marginAssetType}
        setMarginAssetType={walletTransfer.setMarginAssetType}
        marginPairDropOpen={walletTransfer.marginPairDropOpen}
        setMarginPairDropOpen={walletTransfer.setMarginPairDropOpen}
      />
    </div>
  );
}
